import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useClients } from '../hooks/useClients';
import { useWorkouts } from '../hooks/useWorkouts';
import CreateClientModal from '../components/CreateClientModal';
import ViewClientModal from '../components/ViewClientModal';
import EditClientModal from '../components/EditClientModal';
import CreateWorkoutModal from '../components/CreateWorkoutModal';
import WeightModal from '../components/WeightModal';
import BodyMeasurementsModal from '../components/BodyMeasurementsModal';
import ClientPhotoModal from '../components/ClientPhotoModal';
import { Client, ClientCreate, ClientUpdate, WorkoutCreate } from '../types';
import { Plus, Edit, Trash2, Eye, Search, User, Mail, Phone, Calendar, Calendar as CalendarIcon, BarChart3, Scale, Ruler, Camera, Dumbbell, Utensils } from 'lucide-react';
import Navigation from '../components/Navigation';
import { formatPhoneDisplay } from '../utils/formatPhone';

const ClientsPage: React.FC = () => {
  const navigate = useNavigate();
  const { trainer, logout } = useAuth();
  const { 
    clients, 
    isLoading, 
    createClient, 
    updateClient,
    deleteClient, 
    isCreateLoading,
    isUpdateLoading,
    isDeleteLoading 
  } = useClients();
  
  const { 
    workouts, 
    createWorkoutWithCallback, 
    isCreateLoading: isCreateWorkoutLoading 
  } = useWorkouts();
  
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Для просмотра и редактирования
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  // Для создания тренировки
  const [isCreateWorkoutModalOpen, setIsCreateWorkoutModalOpen] = useState(false);
  const [selectedClientForWorkout, setSelectedClientForWorkout] = useState<Client | undefined>(undefined);

  // Для модалки веса
  const [isWeightModalOpen, setIsWeightModalOpen] = useState(false);
  const [selectedClientForWeight, setSelectedClientForWeight] = useState<Client | null>(null);

  // Для модалки замеров
  const [isBodyMeasurementsModalOpen, setIsBodyMeasurementsModalOpen] = useState(false);
  const [selectedClientForBodyMeasurements, setSelectedClientForBodyMeasurements] = useState<Client | null>(null);

  // Для модалки фото
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [selectedClientForPhoto, setSelectedClientForPhoto] = useState<Client | null>(null);

  // Фильтрация клиентов
  const filteredClients = clients.filter(client => {
    const matchesSearch = 
      client.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.phone.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  const handleCreateClient = (data: ClientCreate) => {
    createClient(data);
    setIsCreateModalOpen(false);
  };

  const handleDeleteClient = (clientId: number) => {
    if (window.confirm('Вы уверены, что хотите удалить этого клиента?')) {
      deleteClient(clientId);
    }
  };

  const handleViewClient = (client: Client) => {
    setSelectedClient(client);
    setIsViewModalOpen(true);
  };

  const handleEditClient = (client: Client) => {
    setSelectedClient(client);
    setIsEditModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedClient(null);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedClient(null);
  };

  const handleUpdateClient = (id: number, data: ClientUpdate) => {
    updateClient({ id, data });
    setIsEditModalOpen(false);
    setSelectedClient(null);
  };

  // Функция для получения активной тренировки клиента
  const getActiveWorkoutForClient = (clientId: number) => {
    return workouts.find(workout => 
      !workout.is_completed && 
      workout.clients?.some(client => client.id === clientId)
    );
  };

  // Обработчик создания тренировки
  const handleCreateWorkout = (data: WorkoutCreate) => {
    console.log('ClientsPage: Creating workout with data:', data);
    createWorkoutWithCallback(data, (workout) => {
      console.log('ClientsPage: Workout created successfully:', workout);
      setIsCreateWorkoutModalOpen(false);
      setSelectedClientForWorkout(undefined);
      navigate(`/workouts/${workout.id}`);
    });
  };

  // Обработчик кнопки тренировки для клиента
  const handleWorkoutButton = (client: Client) => {
    // Всегда создаем новую тренировку с предзаполненным клиентом
    setSelectedClientForWorkout(client);
    setIsCreateWorkoutModalOpen(true);
  };

  // Функция для расчета возраста
  const calculateAge = (dateOfBirth: string) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation currentPage="clients" />

      {/* Заголовок страницы */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-6 space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Клиенты</h1>
              <p className="mt-1 text-sm text-gray-500">
                Управление вашими клиентами
              </p>
            </div>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-full sm:w-auto"
            >
              <Plus className="mr-2 h-4 w-4" />
              Добавить клиента
            </button>
          </div>
        </div>
      </div>

      {/* Поиск */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Поиск клиентов..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="text-sm text-gray-500 text-center sm:text-left">
              Показано {filteredClients.length} из {clients.length} клиентов
            </div>
          </div>
        </div>

        {/* Список клиентов */}
        {filteredClients.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <div className="text-gray-400 mb-4">
              <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                <User className="h-8 w-8" />
              </div>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Клиенты не найдены
            </h3>
            <p className="text-gray-500 mb-6">
              {clients.length === 0 
                ? 'У вас пока нет клиентов. Добавьте первого клиента!'
                : 'Попробуйте изменить параметры поиска.'
              }
            </p>
            {clients.length === 0 && (
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="mr-2 h-4 w-4" />
                Добавить первого клиента
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredClients.map((client) => (
              <div key={client.id} className="bg-white rounded-lg shadow overflow-hidden">
                {/* Заголовок карточки */}
                <div className="p-3 sm:p-4 flex flex-col h-full">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-1 truncate">
                        {client.first_name} {client.last_name}
                      </h3>
                      <div className="flex items-center text-xs text-gray-500 mb-1">
                        <Mail size={12} className="mr-1 flex-shrink-0" />
                        <span className="truncate">{client.email}</span>
                      </div>
                    </div>
                    <div className="flex space-x-2 ml-2">
                      <button
                        className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                        title="Просмотр"
                        onClick={() => handleViewClient(client)}
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        className="p-2 text-gray-400 hover:text-green-600 transition-colors"
                        title="Редактировать"
                        onClick={() => handleEditClient(client)}
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteClient(client.id)}
                        disabled={isDeleteLoading}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                        title="Удалить"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Информация о клиенте */}
                  <div className="space-y-1 flex-1">
                    {client.phone && (
                      <div className="flex items-center text-xs text-gray-600">
                        <Phone size={12} className="mr-1.5 text-gray-400 flex-shrink-0" />
                        <span className="truncate">{formatPhoneDisplay(client.phone)}</span>
                      </div>
                    )}
                    
                    {client.date_of_birth && (
                      <div className="flex items-center text-xs text-gray-600">
                        <Calendar size={12} className="mr-1.5 text-gray-400 flex-shrink-0" />
                        <span className="truncate">
                          {new Date(client.date_of_birth).toLocaleDateString('ru-RU')} 
                          ({calculateAge(client.date_of_birth)} лет)
                        </span>
                      </div>
                    )}
                    
                    {client.notes && (
                      <div className="text-xs text-gray-600">
                        <p className="font-medium text-gray-700 mb-1">Заметки:</p>
                        <p className="line-clamp-2">{client.notes}</p>
                      </div>
                    )}
                  </div>

                  {/* Иконки действий */}
                  <div className="flex items-center justify-center space-x-4 mt-3 pt-3 border-t border-gray-200">
                    <div className="cursor-pointer hover:opacity-70 transition-opacity" onClick={() => { setSelectedClientForWeight(client); setIsWeightModalOpen(true); }}>
                      <div className="bg-emerald-100 rounded-full p-2">
                        <Scale size={16} className="text-emerald-600" />
                      </div>
                    </div>
                    
                    <div className="cursor-pointer hover:opacity-70 transition-opacity" onClick={() => { setSelectedClientForBodyMeasurements(client); setIsBodyMeasurementsModalOpen(true); }}>
                      <div className="bg-orange-100 rounded-full p-2">
                        <Ruler size={16} className="text-orange-600" />
                      </div>
                    </div>
                    
                    <div className="cursor-pointer hover:opacity-70 transition-opacity" onClick={() => { setSelectedClientForPhoto(client); setIsPhotoModalOpen(true); }}>
                      <div className="bg-pink-100 rounded-full p-2">
                        <Camera size={16} className="text-pink-600" />
                      </div>
                    </div>
                    
                    <div className="cursor-pointer hover:opacity-70 transition-opacity" onClick={() => navigate(`/clients/${client.id}/stats`)}>
                      <div className="bg-purple-100 rounded-full p-2">
                        <BarChart3 size={16} className="text-purple-600" />
                      </div>
                    </div>
                    
                    <div className="cursor-pointer hover:opacity-70 transition-opacity" onClick={() => navigate(`/clients/${client.id}/nutrition`)}>
                      <div className="bg-green-100 rounded-full p-2">
                        <Utensils size={16} className="text-green-600" />
                      </div>
                    </div>
                    
                    <div className="cursor-pointer hover:opacity-70 transition-opacity" onClick={() => handleWorkoutButton(client)}>
                      <div className="bg-blue-100 rounded-full p-2">
                        <Dumbbell size={16} className="text-blue-600" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Модальные окна */}
      <CreateClientModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateClient}
        isLoading={isCreateLoading}
      />
      <ViewClientModal
        isOpen={isViewModalOpen}
        onClose={handleCloseViewModal}
        client={selectedClient}
        onWeightClick={() => {
          if (selectedClient) {
            setSelectedClientForWeight(selectedClient);
            setIsWeightModalOpen(true);
          }
        }}
        onMeasurementsClick={() => {
          if (selectedClient) {
            setSelectedClientForBodyMeasurements(selectedClient);
            setIsBodyMeasurementsModalOpen(true);
          }
        }}
        onPhotoClick={() => {
          if (selectedClient) {
            setSelectedClientForPhoto(selectedClient);
            setIsPhotoModalOpen(true);
          }
        }}
        onStatsClick={() => {
          if (selectedClient) {
            navigate(`/clients/${selectedClient.id}/stats`);
          }
        }}
        onNutritionClick={() => {
          if (selectedClient) {
            navigate(`/clients/${selectedClient.id}/nutrition`);
          }
        }}
        onWorkoutClick={() => {
          if (selectedClient) {
            handleWorkoutButton(selectedClient);
          }
        }}
      />
      <EditClientModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        client={selectedClient}
        onSubmit={handleUpdateClient}
        isLoading={isUpdateLoading}
      />
      
      {/* Модальное окно создания тренировки */}
      <CreateWorkoutModal
        isOpen={isCreateWorkoutModalOpen}
        onClose={() => {
          setIsCreateWorkoutModalOpen(false);
          setSelectedClientForWorkout(undefined);
        }}
        onSubmit={handleCreateWorkout}
        isLoading={isCreateWorkoutLoading}
        preSelectedClient={selectedClientForWorkout}
      />

      {/* Модальное окно веса */}
      <WeightModal
        isOpen={isWeightModalOpen}
        onClose={() => { setIsWeightModalOpen(false); setSelectedClientForWeight(null); }}
        clientId={selectedClientForWeight?.id || 0}
      />

      {/* Модальное окно замеров */}
      <BodyMeasurementsModal
        isOpen={isBodyMeasurementsModalOpen}
        onClose={() => { setIsBodyMeasurementsModalOpen(false); setSelectedClientForBodyMeasurements(null); }}
        clientId={selectedClientForBodyMeasurements?.id || 0}
      />

      {/* Модальное окно фото */}
      <ClientPhotoModal
        isOpen={isPhotoModalOpen}
        onClose={() => { setIsPhotoModalOpen(false); setSelectedClientForPhoto(null); }}
        client={selectedClientForPhoto as Client}
      />
    </div>
  );
};

export default ClientsPage; 